import { token } from '@atlaskit/tokens';
import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import Button from '@atlaskit/button';
import { ConfluenceLogo } from '@atlaskit/logo';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { ExperienceContext, ExperienceTrackerContext } from '@atlassian/experience-tracker';

import { ExperienceReasons } from '../experience-tracker/ExperienceNames';
import { type useUrl } from './useUrl';
import { LoginToConfluenceIcon } from '../assets/LoginPageIconSVG';
import messages from './messages';

const LOGIN_ISSUES_RELATED_TO_BROWSER_COOKIES =
	'https://support.atlassian.com/atlassian-account/docs/login-issues-related-to-browser-cookies/';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'column',
	fontSize: 'medium',
	height: '500px',
	justifyContent: 'space-around',
	margin: 'auto',
	position: 'relative',
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLoginPageIconSVG = styled(LoginToConfluenceIcon)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		marginTop: token('space.300', '24px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ParagraphLogin = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'': {
		fontSize: 'x-large',
		marginTop: token('space.300', '24px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonLoginToConfluenceNewTab = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		fontWeight: 500,
		marginTop: token('space.300', '24px'),
		marginBottom: token('space.150', '12px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledParagraph = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'': {
		marginTop: token('space.150', '12px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLink = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'': {
		display: 'block',
	},
});

export type ErrorScreenProps = {
	standaloneConfluenceUrl: ReturnType<typeof useUrl>['standaloneConfluenceUrl'];
};

export const ErrorScreen = ({ standaloneConfluenceUrl }: ErrorScreenProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const experience = useContext(ExperienceContext);

	const { formatMessage } = useIntl();

	useEffect(() => {
		experienceTracker.abort({
			name: experience,
			reason: ExperienceReasons.UNABLE_TO_DISPLAY_CONTENT,
		});
	}, [experience, experienceTracker]);

	return (
		<Container data-testid="error-screen-container">
			<ConfluenceLogo appearance="brand" size="xlarge" />
			<StyledLoginPageIconSVG />
			<ParagraphLogin>{formatMessage(messages.paragraphLogin)}</ParagraphLogin>
			<ButtonLoginToConfluenceNewTab
				appearance="primary"
				aria-label={formatMessage(messages.buttonLoginToConfluenceNewTab)}
				iconAfter={<ShortcutIcon label="" size="medium" />}
				href={standaloneConfluenceUrl ?? ''}
				target="_blank"
			>
				{formatMessage(messages.buttonLoginToConfluenceNewTab)}
			</ButtonLoginToConfluenceNewTab>
			<StyledParagraph>{formatMessage(messages.paragraphAlreadyLoggedIn)}</StyledParagraph>
			<StyledParagraph>
				{formatMessage(messages.paragraphStillHavingTrouble, {
					link: (
						<StyledLink
							href={LOGIN_ISSUES_RELATED_TO_BROWSER_COOKIES}
							target="_blank"
							aria-label={formatMessage(messages.linkAriaLabel)}
						>
							{formatMessage(messages.linkTroubleshootingText)}
						</StyledLink>
					),
				})}
			</StyledParagraph>
		</Container>
	);
};
